import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import translationEn from '@/translations/en/hello.json';
import commonEn from '@/translations/en/common.json';
import translationPl from '@/translations/pl/hello.json';
import commonPl from '@/translations/pl/common.json';
import translationEs from '@/translations/es/hello.json';
import commonEs from '@/translations/es/common.json';
import translationNl from '@/translations/nl/hello.json';
import commonNl from '@/translations/nl/common.json';
import translationFr from '@/translations/fr/hello.json';
import commonFr from '@/translations/fr/common.json';
import translationDe from '@/translations/de/hello.json';
import commonDe from '@/translations/de/common.json';

const LANGUAGES = {
  EN: 'en',
  PL: 'pl',
  ES: 'es',
  NL: 'nl',
  FR: 'fr',
  DE: 'de',
} as const;

const SUPPORTED_LNGS = Object.values(LANGUAGES);
const STORAGE_KEY = 'language';

/** Normalize any incoming lang tag to one of SUPPORTED_LNGS, or 'en'. */
function normalizeLng(raw: string | null | undefined): string {
  if (!raw) return 'en';
  const lower = raw.toLowerCase();
  const base = lower.split(/[-_]/)[0];
  return (SUPPORTED_LNGS as readonly string[]).includes(base) ? base : 'en';
}

/** Read persisted language from localStorage, falling back to browser then 'en'. */
function getStoredLanguage(): string {
  try {
    if (typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return normalizeLng(saved);
    }
  } catch {
    // ignore (SSR or storage disabled)
  }
  try {
    if (typeof navigator !== 'undefined' && navigator.language) {
      return normalizeLng(navigator.language);
    }
  } catch {
    // ignore
  }
  return 'en';
}

export const resources = {
  [LANGUAGES.EN]: {
    translation: {
      ...translationEn,
      ...commonEn,
    },
  },
  [LANGUAGES.PL]: {
    translation: {
      ...translationPl,
      ...commonPl,
    },
  },
  [LANGUAGES.ES]: {
    translation: {
      ...translationEs,
      ...commonEs,
    },
  },
  [LANGUAGES.NL]: {
    translation: {
      ...translationNl,
      ...commonNl,
    },
  },
  [LANGUAGES.FR]: {
    translation: {
      ...translationFr,
      ...commonFr,
    },
  },
  [LANGUAGES.DE]: {
    translation: {
      ...translationDe,
      ...commonDe,
    },
  },
};

i18n.use(initReactI18next).init({
  lng: getStoredLanguage(),
  fallbackLng: 'en',
  supportedLngs: SUPPORTED_LNGS,
  nonExplicitSupportedLngs: true,
  resources,
  interpolation: {
    escapeValue: false,
  },
  returnNull: false,
});

// Persist language choice so it survives reloads and route changes.
i18n.on('languageChanged', (lng: string) => {
  const normalized = normalizeLng(lng);
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, normalized);
    }
  } catch {
    // ignore (storage disabled)
  }
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('lang', normalized);
  }
});

// Set the initial <html lang> attribute so the very first render matches.
if (typeof document !== 'undefined') {
  document.documentElement.setAttribute('lang', normalizeLng(i18n.language));
}

export { normalizeLng, getStoredLanguage, SUPPORTED_LNGS };
